import { MyCardsService } from "./api";

export function oneOf(allItems, items) {
  for (let item of items) {
    if (allItems.includes(item)) {
      return true;
    }
  }
  return false;
}

function makeChecker(permission) {
  return function (userPermissions) {
    return userPermissions.includes(permission);
  };
}

export function hasAnyCategoryAccess(userPermissions) {
  return oneOf(userPermissions, [
    "content3__cats__exchange",
    "content3__cats__media",
    "content3__cats__partner",
    "content3__cats__system",
    "content3__cats__aggregate",
    "content3__cats__goods"
  ]);
}

export const hasExchangeCampaignAccess = makeChecker(
  "content3__campaigns__exchange"
);

export function hasAnyCampaignAccess(userPermissions) {
  return oneOf(userPermissions, [
    "content3__campaigns__exchange",
    "content3__campaigns__media",
    "content3__campaigns__system",
    "content3__campaigns__goods"
  ]);
}

export async function canSelectCampaign(userPermissions) {
  const userCards = await MyCardsService();
  return userCards.length != 0 || hasAnyCampaignAccess(userPermissions);
}

export function canViewDeleted(userPermissions) {
  return userPermissions.includes("content3__filters__deleted");
}

export function allowAreaFilter(userPermissions) {
  return userPermissions.includes("content3__filters__area");
}

export function allowDeviceFilter(userPermissions) {
  return userPermissions.includes("content3__filters__device");
}

export function allowPaymentFilter(userPermissions) {
  return userPermissions.includes("content3__filters__payment");
}

export function allowGeoFilter(userPermissions) {
  return userPermissions.includes("content3__filters__geo");
}

export function allowEditorsNameFilter(userPermissions) {
  return userPermissions.includes("content3__filters__editors_name");
}

export function allowEditorsGroupFilter(userPermissions) {
  return userPermissions.includes("content3__filters__editors_group");
}

export function allowAuthorsFilter(userPermissions) {
  return userPermissions.includes("content3__filters__authors");
}

export function allowAnyAuthorFilter(userPermissions) {
  return oneOf(userPermissions, [
    "content3__filters__editors_group",
    "content3__filters__editors_name"
  ]);
}

export function allowTemplates(userPermissions) {
  return userPermissions.includes("content3__filters__templates");
}

export function canPayoutTeaser(userPermissions) {
  return userPermissions.includes("content3__actions__can_pay");
}

export function canFilterByDate(userPermissions) {
  return userPermissions.includes("content3__filters__date_create");
}

export const canSortTtl = makeChecker("content3__sort__ttl");
export const canSortRankMobile = makeChecker("content3__sort__rank_mobile");
export const canSortRankDesktop = makeChecker("content3__sort__rank_desktop");
export const canSortCpc = makeChecker("content3__sort__cpc");
export const canSortKpi = makeChecker("content3__sort__kpi");

export const canSortClicks = makeChecker("content3__sort__clicks");
export const canSortClicksTop = makeChecker("content3__sort__clicks_top");
export const canSortImportance = makeChecker("content3__sort__importance");

export const canSortShows = makeChecker("content3__sort__shows");
export const canSortShowsTop = makeChecker("content3__sort__shows_top");

export const canSortCtrTop = makeChecker("content3__sort__ctr_top");

export const canRestart = makeChecker("content3__actions__restart");
export const canRestartAll = makeChecker("content3__actions__restart_all");

export const canViewHistory = makeChecker("content3__view__history");
export const canViewGeo = makeChecker("content3__view__geo");
export const canViewKpi = makeChecker("content3__view__kpi");
export const canViewDevice = makeChecker("content3__view__device");
export const canViewImportance = makeChecker("content3__view__importance");
export const canViewStat = makeChecker("content3__view__stat");

export const canStartStop = makeChecker("content3__actions__start_stop");
export const canStartStopAll = makeChecker("content3__actions__start_stop_all");

export const canViewFullMiniStat = makeChecker(
  "content3__view__full_mini_stat"
);

export const canChangeArea = makeChecker("content3__actions__change_area");

export const canViewPayoutStatus = makeChecker("content3__view__payout_status");
export const canViewPayoutStatusAll = makeChecker(
  "content3__view__payout_status_all"
);

export const canViewCpc = makeChecker("content3__view__cpc");
export const canChangeCpc = makeChecker("content3__actions__change_cpc");

export const canEditAfterPay = makeChecker("content3__actions__edit_after_pay");
export const canEditAllTeasers = makeChecker(
  "content3__actions__edit_all_teasers"
);

export const canAddTeaser = makeChecker("content3__actions__can_add");
export const canViewAggregator = makeChecker("content3__view__aggregator");

export const canViewBuyout = makeChecker("content3__view__buyout");
export const canViewChangelog = makeChecker("content3__view__changelog");
export const canViewNews = makeChecker("content3__view__news");
export const canViewCategories = makeChecker("content3__view__categories");

export const canModerateExchange = makeChecker(
  "content3__actions_moderate_exchange"
);
export const canModerateMedia = makeChecker("content3__actions_moderate_media");
export const canModerateGoods = makeChecker("content3__actions_moderate_goods");
export const canModerateAny = function (permissions) {
  return (
    canModerateExchange(permissions) ||
    canModerateGoods(permissions) ||
    canModerateMedia(permissions)
  );
};

export const canSetAnyStatus = makeChecker("content3__actions__set_any_status");
export const canSkipModeration = makeChecker(
  "content3__actions_skip_moderation"
);

export const canChangeCid = makeChecker("content3__actions__change_cid");

export const allowExCards = makeChecker("content3__cards__exchange");
export const allowMediaCards = makeChecker("content3__cards__media");
export const allowAllCards = function (permissions) {
  return allowExCards(permissions) && allowMediaCards(permissions);
};
export const allowAnyCards = function (permissions) {
  return allowExCards(permissions) || allowMediaCards(permissions);
};

export const allowGroupActions = makeChecker(
  "content3__actions__group_actions"
);

export const canChangeK = makeChecker("content3__cards__change_k");

export const canGenerateAiTitle = makeChecker("content3__actions__generate_ai_title");